import { useCallback, useEffect, useRef } from 'react'

/** Print only the loaded file. The iframe, listeners and timeout belong to this viewer. */
export function usePrint(url: string | undefined, kind: 'pdf' | 'image' | undefined, title: string) {
    const cleanupRef = useRef<() => void>(() => undefined)
    useEffect(() => () => cleanupRef.current(), [])
    return useCallback(() => {
        if (!url || !kind) return
        cleanupRef.current()
        if (
            kind === 'pdf' &&
            /Safari/.test(navigator.userAgent) &&
            !/Chrome|Chromium|Android/.test(navigator.userAgent)
        ) {
            // Safari's PDF viewer prints from a separate window, as in the reference implementation.
            const popup = window.open(url, '_blank')
            if (!popup) return
            let printed = false
            const cleanup = () => {
                clearTimeout(timer)
                popup.removeEventListener('load', tryPrint)
                popup.removeEventListener('afterprint', cleanup)
                window.removeEventListener('beforeunload', cleanup)
            }
            const tryPrint = () => {
                if (printed || popup.closed) return
                printed = true
                try {
                    popup.focus()
                    popup.print()
                } catch {
                    cleanup()
                }
            }
            const timer = setTimeout(tryPrint, 900)
            popup.addEventListener('load', tryPrint, { once: true })
            popup.addEventListener('afterprint', cleanup, { once: true })
            window.addEventListener('beforeunload', cleanup, { once: true })
            cleanupRef.current = cleanup
            return
        }
        const frame = document.createElement('iframe')
        frame.title = title
        frame.style.cssText = 'position:fixed;width:0;height:0;border:0;left:-9999px;'
        let cancelled = false
        const cleanup = () => {
            if (cancelled) return
            cancelled = true
            clearTimeout(timeout)
            window.removeEventListener('afterprint', cleanup)
            frame.contentWindow?.removeEventListener('afterprint', cleanup)
            frame.remove()
        }
        const timeout = setTimeout(cleanup, 120000)
        cleanupRef.current = cleanup
        window.addEventListener('afterprint', cleanup)
        frame.onload = () => {
            if (cancelled) return
            const print = () => {
                if (cancelled) return
                try {
                    frame.contentWindow?.addEventListener('afterprint', cleanup)
                    frame.contentWindow?.focus()
                    frame.contentWindow?.print()
                } catch {
                    cleanup()
                }
            }
            if (kind === 'image') {
                const image = frame.contentDocument?.querySelector('img')
                if (!image) return cleanup()
                if (image.complete) print()
                else {
                    image.onload = print
                    image.onerror = cleanup
                }
            } else print()
        }
        // URL is a locally created object URL, never server-supplied markup.
        if (kind === 'image') {
            frame.srcdoc = `<html><head><style>body{margin:0}img{max-width:100%;height:auto}</style></head><body><img src="${url}" alt=""></body></html>`
        } else frame.src = url
        document.body.appendChild(frame)
    }, [url, kind, title])
}
